import url from 'url';

import validate from 'validate.js';

import customFetch from 'dpl/util/customFetch';
import { CONSTRAINT_MESSAGES, EMAIL_CONSTRAINTS } from 'dpl/util/validation/constraints';
import { isEmpty } from 'dpl/util/object';

export function sanitizePrice(value) {
  return typeof value === 'string' ? parseFloat(value.replace(/\$/g, '')) : value;
}

const _convertErrorMessages = validate.convertErrorMessages;

validate.convertErrorMessages = function customConvertErrorMessages(errors, options) {
  options.prettify = (attribute) => {
    const errorInfo = errors.find(e => e.attribute === attribute);
    if (errorInfo && errorInfo.options.fieldName) {
      attribute = errorInfo.options.fieldName;
    }

    return validate.prettify(attribute);
  };

  // delegating call to original function
  const ret = _convertErrorMessages.call(this, errors, options);

  ret.forEach((r) => { r.error && (r.error = validate.capitalize(r.error)); });

  return ret;
};

// Custom validators to extend validate.js
validate.validators.truthy = (value) => {
  if (!value) {
    return CONSTRAINT_MESSAGES.REQUIRED_FIELD;
  }

  return null;
};

validate.validators.price = (value) => {
  const excludingDollarSign = sanitizePrice(value);

  if (!validate.isNumber(excludingDollarSign)) {
    return `${CONSTRAINT_MESSAGES.INVALID} amount.`;
  }

  return null;
};

validate.validators.array = (value) => {
  if (!value || !Array.isArray(value)) {
    return CONSTRAINT_MESSAGES.INVALID;
  }

  if (!value.length) {
    return `${CONSTRAINT_MESSAGES.REQUIRED_SIZE} value.`;
  }

  return null;
};

validate.validators.associationArray = (value) => {
  if (Array.isArray(value) && (value.every(v => isEmpty(v) || !Object.values(v).some(Boolean)))) {
    return CONSTRAINT_MESSAGES.REQUIRED_FIELD;
  }
  return null;
};

validate.validators.boolean = (value) => {
  if (validate.isBoolean(value) || ['true', 'false'].includes(value)) {
    return null;
  }

  return CONSTRAINT_MESSAGES.REQUIRED_FIELD;
};

validate.validators.optionalFormat = (value, options) => {
  if (!value || options.pattern.test(value)) {
    return null;
  }

  return options.message || CONSTRAINT_MESSAGES.INVALID;
};

validate.validators.inverseFormat = (value, options) => {
  if (value && !options.pattern.test(value)) {
    return null;
  }

  return options.message || CONSTRAINT_MESSAGES.INVALID;
};

validate.validators.url = (value, options) => {
  if (!value || url.parse(value).hostname) {
    return null;
  }

  return options.message || `${CONSTRAINT_MESSAGES.INVALID} URL.`;
};

validate.validators.nonEmpty = (value, options = {}) => {
  if (validate.isEmpty(value)) {
    return options.message || `${CONSTRAINT_MESSAGES.REQUIRED_FIELD}`;
  }
  return null;
};

validate.validators.emailAvailable = (() => {
  const emailToAvailability = new Map();

  return (value) => {
    if (!value) {
      return CONSTRAINT_MESSAGES.REQUIRED_FIELD;
    }

    const formatValidation = validate({ email: value }, {
      email: EMAIL_CONSTRAINTS
    });

    if (formatValidation) {
      return formatValidation.email;
    }

    value = value.toLowerCase().trim();

    if (emailToAvailability.has(value)) {
      return emailToAvailability.get(value)
        ? CONSTRAINT_MESSAGES.EMAIL_UNAVAILABLE
        : null;
    }

    return new validate.Promise((resolve) => {
      customFetch(`/api/me/exists.json?email=${value}`)
        .then(r => r.json())
        .then((doesExist) => {
          if (doesExist === false) {
            resolve();
            emailToAvailability.set(value, false);
          } else {
            resolve(CONSTRAINT_MESSAGES.EMAIL_UNAVAILABLE);
            emailToAvailability.set(value, true);
          }
        });
    });
  };
})();

export default validate;
